/**
 * Route manifest (FE-001) — the single source of truth for the SPA's pages.
 *
 * Pure data (no JSX, no imports) so Node scripts can load it directly: scripts/gen-nginx.ts turns it
 * into the nginx allow-list that returns a real 404 status for unknown paths, and the redirect map
 * (legacy-redirects.json) is validated against it. The React route tree, sidebar, breadcrumbs and
 * document titles are all derived from here.
 *
 * Layouts:  app (header + sidebar + breadcrumbs + footer, requires a session)
 *           public (centred card: login, signup, password reset)
 *           print (no chrome; printable issue lists)
 *           status (error / maintenance pages, no session needed)
 *           none (renders its own chrome, e.g. the design-system style guide)
 * Access:   anonymous-only (redirects to / when logged in), public, user, manager (70), admin (90).
 *           The UI guard is a convenience; the API enforces every threshold.
 */

export type LayoutId = 'app' | 'public' | 'print' | 'status' | 'none';
export type Access = 'anonymous-only' | 'public' | 'user' | 'manager' | 'admin';
export type NavSection = 'main' | 'manage' | 'account';

export interface RouteDef {
  id: string;
  /** react-router path; `:name` params. */
  path: string;
  /** Page title; `{param}` is replaced with the route parameter. */
  title: string;
  layout: LayoutId;
  access: Access;
  /** Breadcrumb parent route id. */
  parent?: string;
  /** Shown in the sidebar / user menu. `icon` is a lucide-react icon name. */
  nav?: { section: NavSection; order: number; icon?: string; label?: string };
  /** Regex for each path param (for the nginx 404 allow-list); default `[0-9]+`. */
  params?: Record<string, string>;
  /** Legacy scripts this page replaces (documentation + shells). */
  replaces: string[];
}

export const ROUTES = [
  // ------------------------------------------------------------------ public
  {
    id: 'auth.login',
    path: '/login',
    title: 'Log in',
    layout: 'public',
    access: 'anonymous-only',
    replaces: [
      'login_page.php',
      'login_password_page.php',
      'login.php',
      'login_anon.php',
      'login_cookie_test.php',
    ],
  },
  {
    id: 'auth.signup',
    path: '/signup',
    title: 'Sign up for a new account',
    layout: 'public',
    access: 'anonymous-only',
    replaces: ['signup_page.php', 'signup.php'],
  },
  {
    id: 'auth.lost-password',
    path: '/lost-password',
    title: 'Reset your password',
    layout: 'public',
    access: 'anonymous-only',
    replaces: ['lost_pwd_page.php', 'lost_pwd.php'],
  },
  {
    id: 'auth.reset-password',
    path: '/reset-password',
    title: 'Choose a new password',
    layout: 'public',
    access: 'public',
    replaces: [],
  },
  {
    id: 'auth.activate',
    path: '/activate',
    title: 'Activate your account',
    layout: 'public',
    access: 'public',
    replaces: ['verify.php'],
  },
  {
    id: 'auth.logout',
    path: '/logout',
    title: 'Logging out',
    layout: 'status',
    access: 'public',
    replaces: ['logout_page.php'],
  },

  // ------------------------------------------------------------------ status
  {
    id: 'status.maintenance',
    path: '/maintenance',
    title: 'Down for maintenance',
    layout: 'status',
    access: 'public',
    replaces: [],
  },
  {
    id: 'status.error',
    path: '/error',
    title: 'Something went wrong',
    layout: 'status',
    access: 'public',
    replaces: [],
  },

  // ------------------------------------------------------------------ issues
  {
    id: 'home',
    path: '/',
    title: 'My view',
    layout: 'app',
    access: 'user',
    nav: { section: 'main', order: 10, icon: 'LayoutDashboard' },
    replaces: ['my_view_page.php', 'index.php', 'main_page.php'],
  },
  {
    id: 'select-project',
    path: '/select-project',
    title: 'Choose a project',
    layout: 'app',
    access: 'user',
    parent: 'home',
    replaces: ['login_select_proj_page.php', 'set_project.php'],
  },
  {
    id: 'issues.list',
    path: '/issues',
    title: 'View issues',
    layout: 'app',
    access: 'user',
    parent: 'home',
    nav: { section: 'main', order: 20, icon: 'ListChecks' },
    replaces: ['view_all_bug_page.php', 'view_all_set.php', 'search.php'],
  },
  {
    id: 'issues.new',
    path: '/issues/new',
    title: 'Report issue',
    layout: 'app',
    access: 'user',
    parent: 'issues.list',
    nav: { section: 'main', order: 30, icon: 'SquarePen' },
    replaces: ['bug_report_page.php', 'bug_report.php'],
  },
  {
    id: 'issues.filter',
    path: '/issues/filter',
    title: 'Advanced filter',
    layout: 'app',
    access: 'user',
    parent: 'issues.list',
    replaces: ['view_filters_page.php'],
  },
  {
    id: 'issues.bulk',
    path: '/issues/bulk',
    title: 'Update multiple issues',
    layout: 'app',
    access: 'user',
    parent: 'issues.list',
    replaces: [
      'bug_actiongroup_page.php',
      'bug_actiongroup_ext_page.php',
      'bug_actiongroup.php',
      'bug_actiongroup_ext.php',
    ],
  },
  {
    id: 'issues.export',
    path: '/issues/export',
    title: 'Export issues',
    layout: 'app',
    access: 'user',
    parent: 'issues.list',
    replaces: ['csv_export.php', 'excel_xml_export.php', 'print_all_bug_page_word.php'],
  },
  {
    id: 'issues.print',
    path: '/issues/print',
    title: 'Print issues',
    layout: 'print',
    access: 'user',
    replaces: ['print_all_bug_page.php'],
  },
  {
    id: 'issues.view',
    path: '/issues/:issueId',
    title: 'Issue {issueId}',
    layout: 'app',
    access: 'user',
    parent: 'issues.list',
    replaces: ['view.php', 'bug_view_page.php', 'bug_view_advanced_page.php', 'jump_to_bug.php'],
  },
  {
    id: 'issues.edit',
    path: '/issues/:issueId/edit',
    title: 'Edit issue {issueId}',
    layout: 'app',
    access: 'user',
    parent: 'issues.view',
    replaces: ['bug_update_page.php', 'bug_update.php'],
  },
  {
    id: 'issues.status',
    path: '/issues/:issueId/status',
    title: 'Change status of issue {issueId}',
    layout: 'app',
    access: 'user',
    parent: 'issues.view',
    replaces: ['bug_change_status_page.php'],
  },
  {
    id: 'issues.reminder',
    path: '/issues/:issueId/reminder',
    title: 'Send a reminder',
    layout: 'app',
    access: 'user',
    parent: 'issues.view',
    replaces: ['bug_reminder_page.php', 'bug_reminder.php'],
  },
  {
    id: 'issues.graph',
    path: '/issues/:issueId/graph',
    title: 'Relationship graph',
    layout: 'app',
    access: 'user',
    parent: 'issues.view',
    replaces: ['bug_relationship_graph.php'],
  },
  {
    id: 'issues.revisions',
    path: '/issues/:issueId/revisions',
    title: 'Issue {issueId} revisions',
    layout: 'app',
    access: 'user',
    parent: 'issues.view',
    replaces: ['bug_revision_view_page.php'],
  },
  {
    id: 'notes.edit',
    path: '/notes/:noteId/edit',
    title: 'Edit note',
    layout: 'app',
    access: 'user',
    parent: 'issues.list',
    replaces: ['bugnote_edit_page.php', 'bugnote_update.php'],
  },
  {
    id: 'notes.revisions',
    path: '/notes/:noteId/revisions',
    title: 'Note revisions',
    layout: 'app',
    access: 'user',
    parent: 'issues.list',
    replaces: ['bug_revision_view_page.php'],
  },
  {
    id: 'revisions.view',
    path: '/revisions/:revisionId',
    title: 'Revision',
    layout: 'app',
    access: 'user',
    parent: 'issues.list',
    replaces: ['bug_revision_view_page.php'],
  },
  {
    id: 'files.download',
    path: '/files/:fileId',
    title: 'Download file',
    layout: 'app',
    access: 'user',
    parent: 'home',
    replaces: ['file_download.php'],
  },
  {
    id: 'filters.list',
    path: '/filters',
    title: 'Saved filters',
    layout: 'app',
    access: 'user',
    parent: 'issues.list',
    replaces: ['manage_filter_page.php'],
  },
  {
    id: 'filters.new',
    path: '/filters/new',
    title: 'Save current filter',
    layout: 'app',
    access: 'user',
    parent: 'filters.list',
    replaces: ['query_store_page.php', 'query_store.php'],
  },
  {
    id: 'filters.edit',
    path: '/filters/:filterId/edit',
    title: 'Edit filter',
    layout: 'app',
    access: 'user',
    parent: 'filters.list',
    replaces: ['manage_filter_edit_page.php', 'manage_filter_edit_update.php'],
  },
  {
    id: 'filters.permalink',
    path: '/filters/permalink',
    title: 'Filter permalink',
    layout: 'app',
    access: 'user',
    parent: 'issues.list',
    replaces: ['permalink_page.php'],
  },

  // ------------------------------------------------------------------ project information
  {
    id: 'changelog',
    path: '/changelog',
    title: 'Change log',
    layout: 'app',
    access: 'user',
    parent: 'home',
    nav: { section: 'main', order: 40, icon: 'History' },
    replaces: ['changelog_page.php'],
  },
  {
    id: 'roadmap',
    path: '/roadmap',
    title: 'Roadmap',
    layout: 'app',
    access: 'user',
    parent: 'home',
    nav: { section: 'main', order: 50, icon: 'Map' },
    replaces: ['roadmap_page.php'],
  },
  {
    id: 'summary',
    path: '/summary',
    title: 'Summary',
    layout: 'app',
    access: 'user',
    parent: 'home',
    nav: { section: 'main', order: 60, icon: 'ChartColumn' },
    replaces: ['summary_page.php'],
  },
  {
    id: 'summary.trends',
    path: '/summary/trends',
    title: 'Issue trends',
    layout: 'app',
    access: 'user',
    parent: 'summary',
    replaces: ['plugin.php?page=MantisGraph/issues_trend_page'],
  },
  {
    id: 'summary.graphs',
    path: '/summary/graphs',
    title: 'Graphs',
    layout: 'app',
    access: 'user',
    parent: 'summary',
    replaces: ['plugin.php?page=MantisGraph/*_graph'],
  },
  {
    id: 'documents',
    path: '/documents',
    title: 'Project documents',
    layout: 'app',
    access: 'user',
    parent: 'home',
    nav: { section: 'main', order: 70, icon: 'FileText' },
    replaces: ['proj_doc_page.php'],
  },
  {
    id: 'documents.new',
    path: '/documents/new',
    title: 'Upload document',
    layout: 'app',
    access: 'user',
    parent: 'documents',
    replaces: ['proj_doc_add_page.php', 'proj_doc_add.php'],
  },
  {
    id: 'documents.edit',
    path: '/documents/:documentId/edit',
    title: 'Edit document',
    layout: 'app',
    access: 'user',
    parent: 'documents',
    replaces: ['proj_doc_edit_page.php', 'proj_doc_update.php'],
  },
  {
    id: 'news',
    path: '/news',
    title: 'News',
    layout: 'app',
    access: 'user',
    parent: 'home',
    replaces: ['news_list_page.php'],
  },
  {
    id: 'news.new',
    path: '/news/new',
    title: 'Post news',
    layout: 'app',
    access: 'manager',
    parent: 'news',
    replaces: ['news_menu_page.php', 'news_add.php'],
  },
  {
    id: 'news.view',
    path: '/news/:newsId',
    title: 'News item',
    layout: 'app',
    access: 'user',
    parent: 'news',
    replaces: ['news_view_page.php'],
  },
  {
    id: 'news.edit',
    path: '/news/:newsId/edit',
    title: 'Edit news',
    layout: 'app',
    access: 'manager',
    parent: 'news',
    replaces: ['news_edit_page.php', 'news_update.php'],
  },
  {
    id: 'billing',
    path: '/billing',
    title: 'Time tracking',
    layout: 'app',
    access: 'user',
    parent: 'home',
    replaces: ['billing_page.php', 'billing_export_to_csv.php', 'billing_export_to_excel.php'],
  },
  {
    id: 'tags.view',
    path: '/tags/:tagId',
    title: 'Tag',
    layout: 'app',
    access: 'user',
    parent: 'issues.list',
    replaces: ['tag_view_page.php'],
  },
  {
    id: 'users.view',
    path: '/users/:userId',
    title: 'User',
    layout: 'app',
    access: 'user',
    parent: 'home',
    replaces: ['view_user_page.php'],
  },

  // ------------------------------------------------------------------ my account
  {
    id: 'account',
    path: '/account',
    title: 'My account',
    layout: 'app',
    access: 'user',
    parent: 'home',
    nav: { section: 'account', order: 10, icon: 'User' },
    replaces: ['account_page.php', 'account_update.php', 'account_delete.php', 'verify_email.php'],
  },
  {
    id: 'account.preferences',
    path: '/account/preferences',
    title: 'Preferences',
    layout: 'app',
    access: 'user',
    parent: 'account',
    nav: { section: 'account', order: 20, icon: 'SlidersHorizontal' },
    replaces: ['account_prefs_page.php', 'account_prefs_update.php', 'account_prefs_reset.php'],
  },
  {
    id: 'account.columns',
    path: '/account/columns',
    title: 'Manage columns',
    layout: 'app',
    access: 'user',
    parent: 'account',
    nav: { section: 'account', order: 30, icon: 'Columns3' },
    replaces: ['account_manage_columns_page.php'],
  },
  {
    id: 'account.profiles',
    path: '/account/profiles',
    title: 'Profiles',
    layout: 'app',
    access: 'user',
    parent: 'account',
    nav: { section: 'account', order: 40, icon: 'Monitor' },
    replaces: ['account_prof_menu_page.php', 'account_prof_update.php'],
  },
  {
    id: 'account.profiles.edit',
    path: '/account/profiles/:profileId/edit',
    title: 'Edit profile',
    layout: 'app',
    access: 'user',
    parent: 'account.profiles',
    replaces: ['account_prof_edit_page.php'],
  },
  {
    id: 'account.tokens',
    path: '/account/tokens',
    title: 'API tokens',
    layout: 'app',
    access: 'user',
    parent: 'account',
    nav: { section: 'account', order: 50, icon: 'KeyRound' },
    replaces: ['api_tokens_page.php', 'api_token_create.php', 'api_token_revoke.php'],
  },
  {
    id: 'account.sponsorships',
    path: '/account/sponsorships',
    title: 'My sponsorships',
    layout: 'app',
    access: 'user',
    parent: 'account',
    replaces: ['account_sponsor_page.php', 'account_sponsor_update.php'],
  },

  // ------------------------------------------------------------------ manage
  {
    id: 'manage',
    path: '/manage',
    title: 'Manage',
    layout: 'app',
    access: 'manager',
    parent: 'home',
    nav: { section: 'main', order: 90, icon: 'Settings' },
    replaces: ['manage_overview_page.php'],
  },
  {
    id: 'manage.users',
    path: '/manage/users',
    title: 'Manage users',
    layout: 'app',
    access: 'admin',
    parent: 'manage',
    nav: { section: 'manage', order: 10, label: 'Users' },
    replaces: ['manage_user_page.php', 'manage_user_prune.php'],
  },
  {
    id: 'manage.users.new',
    path: '/manage/users/new',
    title: 'Create user',
    layout: 'app',
    access: 'admin',
    parent: 'manage.users',
    replaces: ['manage_user_create_page.php', 'manage_user_create.php'],
  },
  {
    id: 'manage.users.edit',
    path: '/manage/users/:userId',
    title: 'Edit user',
    layout: 'app',
    access: 'admin',
    parent: 'manage.users',
    replaces: [
      'manage_user_edit_page.php',
      'manage_user_update.php',
      'manage_user_reset.php',
      'manage_user_delete.php',
      'manage_user_impersonate.php',
      'manage_user_proj_add.php',
      'manage_user_proj_delete.php',
    ],
  },
  {
    id: 'manage.projects',
    path: '/manage/projects',
    title: 'Manage projects',
    layout: 'app',
    access: 'manager',
    parent: 'manage',
    nav: { section: 'manage', order: 20, label: 'Projects' },
    replaces: ['manage_proj_page.php'],
  },
  {
    id: 'manage.projects.new',
    path: '/manage/projects/new',
    title: 'Create project',
    layout: 'app',
    access: 'admin',
    parent: 'manage.projects',
    replaces: ['manage_proj_create_page.php', 'manage_proj_create.php'],
  },
  {
    id: 'manage.projects.edit',
    path: '/manage/projects/:projectId',
    title: 'Edit project',
    layout: 'app',
    access: 'manager',
    parent: 'manage.projects',
    replaces: [
      'manage_proj_edit_page.php',
      'manage_proj_update.php',
      'manage_proj_delete.php',
      'manage_proj_*.php',
    ],
  },
  {
    id: 'manage.versions.edit',
    path: '/manage/versions/:versionId',
    title: 'Edit version',
    layout: 'app',
    access: 'manager',
    parent: 'manage.projects',
    replaces: ['manage_proj_ver_edit_page.php', 'manage_proj_ver_update.php'],
  },
  {
    id: 'manage.categories.edit',
    path: '/manage/categories/:categoryId',
    title: 'Edit category',
    layout: 'app',
    access: 'manager',
    parent: 'manage.projects',
    replaces: ['manage_proj_cat_edit_page.php', 'manage_proj_cat_update.php'],
  },
  {
    id: 'manage.custom-fields',
    path: '/manage/custom-fields',
    title: 'Manage custom fields',
    layout: 'app',
    access: 'admin',
    parent: 'manage',
    nav: { section: 'manage', order: 30, label: 'Custom fields' },
    replaces: ['manage_custom_field_page.php', 'manage_custom_field_create.php'],
  },
  {
    id: 'manage.custom-fields.edit',
    path: '/manage/custom-fields/:fieldId',
    title: 'Edit custom field',
    layout: 'app',
    access: 'admin',
    parent: 'manage.custom-fields',
    replaces: [
      'manage_custom_field_edit_page.php',
      'manage_custom_field_update.php',
      'manage_custom_field_delete.php',
      'manage_custom_field_proj_add.php',
    ],
  },
  {
    id: 'manage.profiles',
    path: '/manage/profiles',
    title: 'Global profiles',
    layout: 'app',
    access: 'admin',
    parent: 'manage',
    nav: { section: 'manage', order: 40, label: 'Global profiles' },
    replaces: ['manage_prof_menu_page.php'],
  },
  {
    id: 'manage.tags',
    path: '/manage/tags',
    title: 'Manage tags',
    layout: 'app',
    access: 'manager',
    parent: 'manage',
    nav: { section: 'manage', order: 50, label: 'Tags' },
    replaces: ['manage_tags_page.php', 'manage_tag_view_page.php', 'tag_create.php'],
  },
  {
    id: 'manage.tags.edit',
    path: '/manage/tags/:tagId/edit',
    title: 'Edit tag',
    layout: 'app',
    access: 'manager',
    parent: 'manage.tags',
    replaces: ['tag_update_page.php', 'tag_update.php', 'tag_delete.php'],
  },
  {
    id: 'manage.plugins',
    path: '/manage/plugins',
    title: 'Manage plugins',
    layout: 'app',
    access: 'admin',
    parent: 'manage',
    nav: { section: 'manage', order: 60, label: 'Plugins' },
    replaces: ['manage_plugin_page.php', 'manage_plugin_*.php'],
  },
  {
    id: 'manage.plugins.formatting',
    path: '/manage/plugins/formatting',
    title: 'Text formatting',
    layout: 'app',
    access: 'admin',
    parent: 'manage.plugins',
    replaces: ['plugin.php?page=MantisCoreFormatting/config'],
  },
  {
    id: 'manage.plugins.import-export',
    path: '/manage/plugins/import-export',
    title: 'XML import/export settings',
    layout: 'app',
    access: 'admin',
    parent: 'manage.plugins',
    replaces: ['plugin.php?page=XmlImportExport/config_page'],
  },
  {
    id: 'manage.import',
    path: '/manage/import',
    title: 'Import issues',
    layout: 'app',
    access: 'manager',
    parent: 'manage',
    replaces: ['plugin.php?page=XmlImportExport/import'],
  },
  {
    id: 'manage.config',
    path: '/manage/config',
    title: 'Configuration report',
    layout: 'app',
    access: 'admin',
    parent: 'manage',
    nav: { section: 'manage', order: 70, label: 'Configuration' },
    replaces: ['adm_config_report.php'],
  },
  {
    id: 'manage.config.edit',
    path: '/manage/config/edit',
    title: 'Set configuration option',
    layout: 'app',
    access: 'admin',
    parent: 'manage.config',
    replaces: ['adm_config_page.php', 'adm_config_set.php', 'adm_config_delete.php'],
  },
  {
    id: 'manage.config.permissions',
    path: '/manage/config/permissions',
    title: 'Permissions report',
    layout: 'app',
    access: 'manager',
    parent: 'manage.config',
    replaces: ['adm_permissions_report.php'],
  },
  {
    id: 'manage.config.thresholds',
    path: '/manage/config/thresholds',
    title: 'Workflow thresholds',
    layout: 'app',
    access: 'manager',
    parent: 'manage.config',
    replaces: ['manage_config_work_threshold_page.php'],
  },
  {
    id: 'manage.config.workflow',
    path: '/manage/config/workflow',
    title: 'Workflow transitions',
    layout: 'app',
    access: 'manager',
    parent: 'manage.config',
    replaces: ['manage_config_workflow_page.php'],
  },
  {
    id: 'manage.config.workflow-graph',
    path: '/manage/config/workflow/graph',
    title: 'Workflow graph',
    layout: 'app',
    access: 'manager',
    parent: 'manage.config.workflow',
    replaces: ['manage_config_workflow_graph_page.php'],
  },
  {
    id: 'manage.config.email',
    path: '/manage/config/email',
    title: 'E-mail notifications',
    layout: 'app',
    access: 'manager',
    parent: 'manage.config',
    replaces: ['manage_config_email_page.php'],
  },
  {
    id: 'manage.config.columns',
    path: '/manage/config/columns',
    title: 'Manage columns',
    layout: 'app',
    access: 'manager',
    parent: 'manage.config',
    replaces: ['manage_config_columns_page.php'],
  },
  {
    id: 'manage.system',
    path: '/manage/system',
    title: 'System information',
    layout: 'app',
    access: 'admin',
    parent: 'manage',
    nav: { section: 'manage', order: 80, label: 'System' },
    replaces: [
      'admin/index.php',
      'admin/check/index.php',
      'admin/system_utils.php',
      'admin/db_stats.php',
      'admin/email_queue.php',
      'admin/move_attachments_page.php',
      'admin/test_langs.php',
    ],
  },

  // ------------------------------------------------------------------ developer
  {
    id: 'design-system',
    path: '/design-system/:section?',
    title: 'Design system',
    layout: 'none',
    access: 'public',
    params: { section: '[a-z-]+' },
    replaces: [],
  },
] as const satisfies readonly RouteDef[];

export type RouteId = (typeof ROUTES)[number]['id'];

export const ROUTE_BY_ID: ReadonlyMap<string, RouteDef> = new Map(
  ROUTES.map((r) => [r.id, r as RouteDef]),
);

/** Build a URL for a route: `href('issues.view', { issueId: 12 })` → `/issues/12`. */
export function href(
  id: RouteId,
  params: Record<string, string | number> = {},
  query?: Record<string, string | number | undefined>,
): string {
  const route = ROUTE_BY_ID.get(id);
  if (!route) throw new Error(`unknown route ${id}`);
  let path = route.path.replace(/\/:([a-zA-Z]+)(\?)?/g, (_m, name: string, optional?: string) => {
    const v = params[name];
    if (v === undefined) {
      if (optional) return '';
      throw new Error(`route ${id} needs param ${name}`);
    }
    return `/${encodeURIComponent(String(v))}`;
  });
  if (path === '') path = '/';
  const qs = query
    ? Object.entries(query)
        .filter((e): e is [string, string | number] => e[1] !== undefined && e[1] !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&')
    : '';
  return qs ? `${path}?${qs}` : path;
}

/** Title with `{param}` placeholders filled. */
export function formatTitle(
  route: RouteDef,
  params: Readonly<Record<string, string | undefined>>,
): string {
  return route.title.replace(/\{([a-zA-Z]+)\}/g, (_m, k: string) => params[k] ?? '');
}

/** Breadcrumb chain from home to the route. */
export function ancestry(id: string): RouteDef[] {
  const out: RouteDef[] = [];
  const seen = new Set<string>();
  for (
    let r = ROUTE_BY_ID.get(id);
    r && !seen.has(r.id);
    r = r.parent ? ROUTE_BY_ID.get(r.parent) : undefined
  ) {
    seen.add(r.id);
    out.unshift(r);
  }
  return out;
}
